/**
 * Queue Monitor Repository
 *
 * Manages queue monitoring with pagination support for all tabs.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get, post, del } from 'data/services/apiService';

// ============================================
// Deploy-skew guard (v2 contract 4.3 / decision 19)
// ============================================
// The endpoints guarded below may not exist on an older backend. Each failing
// call console.warns ONCE per session and RESOLVES instead of rejecting, so a
// 30s polling page never raises per-cycle unhandled rejections against an old
// BE. The resolved shape is { data: null } rather than bare null because the
// createRepository custom-thunk wrapper dereferences response.data on the
// success path; a bare null would throw inside the thunk and re-create the
// exact unhandled-rejection noise this catch exists to prevent. Reducer state
// lands on null either way, and page callers detect "unsupported" by the
// missing payload keys (e.g. no `basis`).
const skewWarnings = {};
const catchDeploySkew = (key, message) => (error) => {
	if (!skewWarnings[key]) {
		skewWarnings[key] = true;
		// eslint-disable-next-line no-console
		console.warn(message, error);
	}
	return { data: null };
};

// ============================================
// Create Repository
// ============================================
const queueMonitorRepository = createRepository({
	endpoint: 'violerts-queue/stats',
	name: 'queueMonitor',
	statePath: 'queueMonitor',

	customMethods: {
		/**
		 * Get queue summary statistics
		 * Usage: actions.getSummary()
		 */
		getSummary: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/violerts-queue/stats`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get queue health status
		 * Usage: actions.getHealth()
		 */
		getHealth: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/violerts-queue/health`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get extended statistics
		 * Usage: actions.getExtendedStats()
		 */
		getExtendedStats: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/violerts-queue/stats/extended`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Search properties (for FormAjaxSelect)
		 * Usage: actions.searchProperties({ query: '123 Main' })
		 */
		searchProperties: ({ query, authToken, dispatch }) => {
			const params = { search: query, limit: 20 };
			return get({
				url: `${getApiUrl()}/api/properties/search?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get property job statistics
		 * Usage: actions.getPropertyJobStats({ propertyId: 123, date: '2026-04-03' })
		 */
		getPropertyJobStats: ({ propertyId, date, authToken, dispatch }) => {
			const params = { property_id: propertyId };
			if (date) params.date = date;

			return get({
				url: `${getApiUrl()}/api/violerts-queue/property-stats?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get property job trends
		 * Usage: actions.getPropertyJobTrends({ propertyId: 123, days: 7 })
		 */
		getPropertyJobTrends: ({ propertyId, days = 7, authToken, dispatch }) => {
			const params = { property_id: propertyId, days };

			return get({
				url: `${getApiUrl()}/api/violerts-queue/property-trends?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get top properties by job count
		 * Usage: actions.getTopPropertiesByJobs({ date: '2026-04-03', limit: 20 })
		 */
		getTopPropertiesByJobs: ({ date, limit = 20, authToken, dispatch }) => {
			const params = { limit };
			if (date) params.date = date;

			return get({
				url: `${getApiUrl()}/api/violerts-queue/top-properties?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get paginated pending jobs
		 * Usage: actions.getPendingJobs({ page: 1, per_page: 25, queue: 'default', property_id: 123 })
		 */
		getPendingJobs: ({
			page = 1,
			per_page = 25,
			queue = null,
			property_id = null,
			authToken,
			dispatch,
		}) => {
			const params = { page, per_page };
			if (queue) params.queue = queue;
			if (property_id) params.property_id = property_id;

			return get({
				url: `${getApiUrl()}/api/violerts-queue/pending?${new URLSearchParams(params)}`,
				params,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get paginated processing jobs
		 * Usage: actions.getProcessingJobs({ page: 1, per_page: 25, queue: 'default', property_id: 123 })
		 */
		getProcessingJobs: ({
			page = 1,
			per_page = 25,
			queue = null,
			property_id = null,
			authToken,
			dispatch,
		}) => {
			const params = { page, per_page };
			if (queue) params.queue = queue;
			if (property_id) params.property_id = property_id;

			return get({
				url: `${getApiUrl()}/api/violerts-queue/processing?${new URLSearchParams(params)}`,
				params,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get paginated stuck jobs
		 * Usage: actions.getStuckJobs({ page: 1, per_page: 25, queue: 'default', property_id: 123 })
		 */
		getStuckJobs: ({
			page = 1,
			per_page = 25,
			queue = null,
			property_id = null,
			authToken,
			dispatch,
		}) => {
			const params = { page, per_page };
			if (queue) params.queue = queue;
			if (property_id) params.property_id = property_id;

			return get({
				url: `${getApiUrl()}/api/violerts-queue/stuck?${new URLSearchParams(params)}`,
				params,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get paginated failed jobs
		 * Usage: actions.getFailedJobs({ page: 1, per_page: 25, queue: 'default', hours: 24, property_id: 123, exception_class: 'GuzzleHttp\\Exception\\ConnectException' })
		 */
		getFailedJobs: ({
			page = 1,
			per_page = 25,
			queue = null,
			hours = 36000,
			property_id = null,
			exception_class = null,
			authToken,
			dispatch,
		}) => {
			const params = { page, per_page, hours };
			if (queue) params.queue = queue;
			if (property_id) params.property_id = property_id;
			if (exception_class) params.exception_class = exception_class;

			return get({
				url: `${getApiUrl()}/api/violerts-queue/failed?${new URLSearchParams(params)}`,
				params,
				authToken,
				dispatch,
			});
		},

		/**
		 * Get failure groups by exception class
		 * Usage: actions.getFailedByException({ hours: 168, queue: 'scraper' })
		 */
		getFailedByException: ({ hours = 168, queue = null, authToken, dispatch }) => {
			const params = { hours };
			if (queue) params.queue = queue;
			return get({
				url: `${getApiUrl()}/api/violerts-queue/failed/by-exception?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'failedByException',
					'QueueMonitor: /failed/by-exception unavailable (old backend?) - cause table hidden',
				),
			);
		},

		/**
		 * Get the failures breakdown (v2, event-sourced when covered).
		 * Usage: actions.getFailuresBreakdown({ hours: 168, queue: 'scraper' })
		 * Resolves { data: null } on failure (old/v1 backend without the route);
		 * the page probes once and falls back to getFailedByException.
		 */
		getFailuresBreakdown: ({ hours = 168, queue = null, authToken, dispatch }) => {
			const params = { hours };
			if (queue) params.queue = queue;
			return get({
				url: `${getApiUrl()}/api/violerts-queue/failures/breakdown?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'failuresBreakdown',
					'QueueMonitor: /failures/breakdown unavailable (pre-v2 backend?) - falling back to /failed/by-exception',
				),
			);
		},

		/**
		 * Get per-source hourly metrics from job_metrics (v2 contract 7.2).
		 * Usage: actions.getMetricsHourly({ hours: 48, source: 'bis', queue: 'scraper' })
		 */
		getMetricsHourly: ({
			hours = 48,
			source = null,
			queue = null,
			authToken,
			dispatch,
		}) => {
			const params = { hours };
			if (source) params.source = source;
			if (queue) params.queue = queue;
			return get({
				url: `${getApiUrl()}/api/violerts-queue/metrics/hourly?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'metricsHourly',
					'QueueMonitor: /metrics/hourly unavailable (pre-v2 backend?) - hourly panel hidden',
				),
			);
		},

		/**
		 * Get per-hour failure anomalies (v3 contract 5.1).
		 * Each point flags hours whose failure count exceeds the
		 * anomaly_multiplier x trailing-7d median (anomaly true/false/null).
		 * Usage: actions.getAnomalies({ hours: 168 })
		 * Resolves { data: null } on failure (pre-v3 backend without the
		 * route); the chart renders an empty/warming state.
		 */
		getAnomalies: ({ hours = 168, authToken, dispatch }) => {
			const params = { hours };
			return get({
				url: `${getApiUrl()}/api/violerts-queue/anomalies?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'anomalies',
					'QueueMonitor: /anomalies unavailable (pre-v3 backend?) - anomaly chart hidden',
				),
			);
		},

		/**
		 * Get 30/90-day capacity trend (v3 contract 5.2).
		 * Daily rollups of attempts + failures + failure_rate plus 30d
		 * trend deltas and the peak day.
		 * Usage: actions.getCapacity({ days: 30 })
		 * Resolves { data: null } on failure (pre-v3 backend without the
		 * route); the trend view renders an empty state.
		 */
		getCapacity: ({ days = 30, authToken, dispatch }) => {
			const params = { days };
			return get({
				url: `${getApiUrl()}/api/violerts-queue/capacity?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'capacity',
					'QueueMonitor: /capacity unavailable (pre-v3 backend?) - capacity view hidden',
				),
			);
		},

		/**
		 * Get the email outbox snapshot the outbox health rules read: per-type
		 * pending and aged rows, the users an hourly alert is overdue for, and
		 * today's hourly sends.
		 * Usage: actions.getOutbox()
		 * Resolves { data: null } on failure (backend without the route); the
		 * Email outbox tab and cards stay hidden.
		 */
		getOutbox: ({ authToken, dispatch }) => {
			return get({
				url: `${getApiUrl()}/api/violerts-queue/outbox`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'outbox',
					'QueueMonitor: /outbox unavailable (old backend?) - email outbox view hidden',
				),
			);
		},

		/**
		 * Bulk retry parked failed jobs matching the current filters
		 * (v2 contract 5.2/5.3). camelCase args map to the snake_case request
		 * body. `limit` is the TRUE BOUND the user confirmed: the page always
		 * sends min(N, 500) explicitly and never relies on this default.
		 * Usage: actions.bulkRetryFailedJobs({ hours: 168, queue: null, exceptionClass: null, limit: 47 })
		 */
		bulkRetryFailedJobs: ({
			hours = 168,
			queue = null,
			exceptionClass = null,
			limit = 500,
			authToken,
			dispatch,
		}) => {
			const data = { hours, limit };
			if (queue) data.queue = queue;
			if (exceptionClass) data.exception_class = exceptionClass;
			return post({
				url: `${getApiUrl()}/api/violerts-queue/failed/bulk-retry`,
				data,
				authToken,
				dispatch,
			});
		},

		/**
		 * Retry a failed job
		 * Usage: actions.retryFailedJob({ uuid: '...' })
		 */
		retryFailedJob: ({ uuid, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/violerts-queue/failed/${uuid}/retry`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Delete a failed job
		 * Usage: actions.deleteFailedJob({ uuid: '...' })
		 */
		deleteFailedJob: ({ uuid, authToken, dispatch }) => {
			return del({
				url: `${getApiUrl()}/api/violerts-queue/failed/${uuid}`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Release a stuck job
		 * Usage: actions.releaseJob({ jobId: 123 })
		 */
		releaseJob: ({ jobId, authToken, dispatch }) => {
			return post({
				url: `${getApiUrl()}/api/violerts-queue/jobs/${jobId}/release`,
				authToken,
				dispatch,
			});
		},

		/**
		 * Delete a job
		 * Usage: actions.deleteJob({ jobId: 123 })
		 */
		deleteJob: ({ jobId, authToken, dispatch }) => {
			return del({
				url: `${getApiUrl()}/api/violerts-queue/jobs/${jobId}`,
				authToken,
				dispatch,
			});
		},
	},
});

// ============================================
// Exports
// ============================================
export const {
	name,
	statePath,
	thunks: queueMonitorThunks,
	reducer: queueMonitorReducer,
	actions: queueMonitorActions,
	types: queueMonitorTypes,
	selectors: queueMonitorSelectors,
	useHook: useQueueMonitor,
} = queueMonitorRepository;

export default queueMonitorRepository;
